import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Buildings, UsersThree, ArrowRight, ArrowLeft, CheckCircle, X, Translate, MagnifyingGlass, Compass } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useSeo } from "@/lib/seo";
import { useLanguage } from "@/context/LanguageContext";
import { INDIA_GEO } from "@/data/indiaGeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const toSlug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
const deSlug = (s) => (s || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Compact Levenshtein for fuzzy location guessing.
function lev(a, b) {
  const m = a.length, n = b.length;
  if (!m) return n; if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

function resolveNames(stateSlug, districtSlug) {
  const st = INDIA_GEO.find((s) => toSlug(s.state) === stateSlug);
  if (!st) return { known: false, state: deSlug(stateSlug), district: deSlug(districtSlug) };
  const d = st.districts.find((x) => toSlug(x) === districtSlug);
  return { known: !!d, stateKnown: true, state: st.state, district: d || deSlug(districtSlug) };
}

// Best-guess district matches for an unrecognised query.
function suggestLocations(query, limit = 6) {
  const q = norm(query);
  if (!q) return [];
  const scored = [];
  for (const s of INDIA_GEO) {
    for (const d of s.districts) {
      const key = norm(d);
      const inc = key.includes(q) || q.includes(key);
      scored.push({ state: s.state, district: d, score: inc ? -1 : lev(q, key) });
    }
  }
  scored.sort((a, b) => a.score - b.score);
  return scored.filter((x) => x.score <= 4).slice(0, limit);
}

// 20-language translate bar shown above the article. English is the default;
// the bar is presented as a clear "Translate this page" control.
function ArticleLangBar() {
  const { lang, setLang, resetToEnglish, langs, translating } = useLanguage();
  return (
    <div className="rounded-2xl border border-border/60 bg-accent/30 p-3" data-testid="article-lang-bar">
      <div className="mb-2 flex items-center gap-1.5">
        <Translate size={16} weight="duotone" className="text-primary" />
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground/70">Translate this page</span>
        {lang !== "en" && translating && (
          <span className="ml-1 inline-flex items-center gap-1 text-xs text-primary/80">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-primary" /> translating…
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {langs.filter((l) => l.code !== "en").map((l) => (
          <button key={l.code} onClick={() => setLang(l.code)} data-testid={`article-lang-${l.code}`}
            className={`rounded-full px-2.5 py-1 text-xs transition-colors ${lang === l.code ? "bg-primary text-primary-foreground" : "border border-border/70 bg-white text-foreground/70 hover:border-primary/40"}`}>
            {l.native}
          </button>
        ))}
        {lang !== "en" && (
          <button onClick={resetToEnglish} data-testid="article-lang-reset"
            className="ml-1 inline-flex items-center gap-1 rounded-full bg-foreground px-2.5 py-1 text-xs font-semibold text-background">
            <X size={12} weight="bold" /> English
          </button>
        )}
      </div>
    </div>
  );
}

// Skeleton shimmer shown while a first-time (cold) translation is being fetched.
function ArticleShimmer() {
  return (
    <div className="mt-4 max-w-3xl space-y-3" data-testid="district-article-shimmer">
      <div className="h-5 w-3/4 animate-pulse rounded bg-muted" />
      <div className="space-y-2 pt-1">
        <div className="h-3 w-full animate-pulse rounded bg-muted" />
        <div className="h-3 w-11/12 animate-pulse rounded bg-muted" />
        <div className="h-3 w-4/5 animate-pulse rounded bg-muted" />
      </div>
      <div className="space-y-2 pt-1">
        <div className="h-3 w-full animate-pulse rounded bg-muted" />
        <div className="h-3 w-10/12 animate-pulse rounded bg-muted" />
      </div>
    </div>
  );
}

function UnknownLocation({ rawState, rawDistrict }) {
  const navigate = useNavigate();
  const guessSource = rawDistrict || rawState;
  const [value, setValue] = useState(deSlug(guessSource));
  const suggestions = useMemo(() => suggestLocations(guessSource), [guessSource]);
  useSeo("Location not found — IndiaAlumni", "We couldn't find that location. Search or add it.");

  return (
    <div className="mx-auto max-w-2xl px-5 py-16 text-center" data-testid="location-not-found">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary/15 text-secondary">
        <Compass size={28} weight="duotone" />
      </div>
      <h1 className="mt-5 font-heading text-2xl font-semibold sm:text-3xl">We don't recognise that location yet</h1>
      <p className="mt-2 text-muted-foreground">We couldn't match <span className="font-medium text-foreground">"{deSlug(guessSource)}"</span> to an Indian district. Did you mean one of these?</p>

      {suggestions.length > 0 && (
        <div className="mt-5 flex flex-wrap justify-center gap-2" data-testid="location-suggestions">
          {suggestions.map((m) => (
            <button key={m.state + m.district} onClick={() => navigate(`/india/place/${toSlug(m.state)}/${toSlug(m.district)}`)}
              data-testid={`location-suggestion-${toSlug(m.district)}`}
              className="inline-flex items-center gap-1.5 rounded-full border border-border/70 bg-white px-3.5 py-2 text-sm transition-colors hover:border-primary/50 hover:text-primary">
              <MapPin size={14} className="text-secondary" /> {m.district}, {m.state}
            </button>
          ))}
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-border/60 p-5 text-left">
        <p className="text-sm font-medium">Not in the list? Tell us the location and we'll add it.</p>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input value={value} onChange={(e) => setValue(e.target.value)} className="rounded-full pl-10" data-testid="location-fix-input" placeholder="District, State" />
          </div>
          <Button className="rounded-full" data-testid="location-fix-submit"
            onClick={() => navigate(`/institutions/suggest?location=${encodeURIComponent(value)}`)}>
            Submit location
          </Button>
        </div>
      </div>

      <Link to="/india/locations" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-secondary hover:underline" data-testid="not-found-browse-all">
        <ArrowLeft size={15} /> Browse all states & districts
      </Link>
    </div>
  );
}

export default function IndiaLocationDetail() {
  const { t, lang, translating } = useLanguage();
  const { stateSlug, districtSlug } = useParams();
  const resolved = useMemo(() => resolveNames(stateSlug, districtSlug), [stateSlug, districtSlug]);
  const { state, district, known } = resolved;
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [content, setContent] = useState(null);
  useSeo(known ? `Alumni in ${district}, ${state}` : "Location — IndiaAlumni",
    known ? `Schools, colleges and alumni communities in ${district}, ${state}.` : "Find alumni by location in India.");

  useEffect(() => {
    if (!known) return;
    window.scrollTo(0, 0);
    setData(null); setErr(false); setContent(null);
    axios.get(`${API}/india/location`, { params: { state, district, limit: 60 } })
      .then((r) => setData(r.data)).catch(() => setErr(true));
    axios.get(`${API}/india/district-article`, { params: { state, district } })
      .then((r) => setContent(r.data)).catch(() => setContent({ intro: "", article: "" }));
  }, [state, district, known]);

  if (!known) return <UnknownLocation rawState={stateSlug} rawDistrict={districtSlug} />;

  const institutions = data?.institutions || [];
  const groups = data?.groups || [];
  const paragraphs = (content?.article || "").split(/\n\n+/).filter(Boolean);

  return (
    <div className="mx-auto max-w-7xl px-5 py-10" data-testid="india-location-detail">
      <Link to={`/india/locations/${toSlug(state)}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary" data-testid="detail-back">
        <ArrowLeft size={15} /> {state}
      </Link>
      <div className="mt-3 flex items-center gap-2 text-primary">
        <MapPin size={22} weight="duotone" />
        <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">{district}</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{state}, India{data ? ` · ${data.total_institutions} ${t("institutions")}` : ""}</p>

      {/* Article + 20-language translate bar */}
      <div className="mt-6" data-testid="district-article">
        <ArticleLangBar />
        {content === null ? (
          <div className="mt-4 space-y-2">
            <div className="h-4 w-2/3 animate-pulse rounded bg-muted" />
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="h-3 w-5/6 animate-pulse rounded bg-muted" />
          </div>
        ) : lang !== "en" && translating ? (
          <ArticleShimmer />
        ) : (
          <div className="mt-4 max-w-3xl">
            {content.intro && <p className="font-heading text-lg font-medium text-foreground/90" data-testid="district-intro">{t(content.intro)}</p>}
            {paragraphs.map((p, i) => (
              <p key={i} className="mt-3 leading-relaxed text-muted-foreground">{t(p)}</p>
            ))}
          </div>
        )}
      </div>

      {err ? (
        <p className="mt-10 text-muted-foreground">{t("Could not load this location. Please try again.")}</p>
      ) : data === null ? (
        <p className="mt-10 text-muted-foreground">{t("Loading…")}</p>
      ) : (
        <div className="mt-10 grid gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-4 flex items-center gap-2">
              <Buildings size={18} className="text-secondary" weight="duotone" />
              <h2 className="font-heading text-xl font-medium">{t("Institutions")}</h2>
            </div>
            {institutions.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("No institutions listed here yet.")}</p>
            ) : (
              <div className="space-y-2" data-testid="detail-institutions">
                {institutions.map((inst) => (
                  <Link key={inst.id} to={`/institutions/${inst.id}`} data-testid={`detail-inst-${inst.id}`}
                    className="flex items-center justify-between gap-3 rounded-xl border border-border/60 p-3.5 transition-colors hover:border-primary/50 hover:bg-accent/40">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate font-medium">{inst.name}</span>
                        {inst.verified && <CheckCircle size={14} weight="fill" className="shrink-0 text-secondary" />}
                      </div>
                      <span className="text-xs text-muted-foreground">{[inst.type, inst.location].filter(Boolean).join(" · ")}</span>
                    </div>
                    <ArrowRight size={15} className="shrink-0 text-muted-foreground" />
                  </Link>
                ))}
                {data.total_institutions > institutions.length && (
                  <Link to={`/explore/institutions?country=India&state=${encodeURIComponent(state)}&district=${encodeURIComponent(district)}`} className="inline-flex items-center gap-1 pt-1 text-sm font-medium text-secondary hover:underline">
                    {t("See all")} {data.total_institutions} {t("institutions")} <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            )}
          </div>

          <div>
            <div className="mb-4 flex items-center gap-2">
              <UsersThree size={18} className="text-secondary" weight="duotone" />
              <h2 className="font-heading text-xl font-medium">{t("Alumni communities")}</h2>
            </div>
            {groups.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("No public communities here yet.")}</p>
            ) : (
              <div className="space-y-2" data-testid="detail-groups">
                {groups.map((g) => (
                  <Link key={g.id} to={`/groups/${g.id}`} data-testid={`detail-group-${g.id}`}
                    className="block rounded-xl border border-border/60 p-3.5 transition-colors hover:border-primary/50 hover:bg-accent/40">
                    <span className="block truncate font-medium">{g.name}</span>
                    {g.member_count > 0
                      ? <span className="text-xs text-muted-foreground">{g.member_count} {t(g.member_count === 1 ? "member" : "members")}</span>
                      : <span className="text-xs text-muted-foreground">{t("New community")}</span>}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="mt-10 rounded-2xl border border-dashed border-secondary/40 bg-secondary/5 p-5 text-center" data-testid="not-found-cta">
        <p className="text-sm font-medium text-foreground">{t("Not found your school / college?")}</p>
        <Link to="/institutions/suggest" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-secondary hover:underline" data-testid="suggest-from-detail">
          {t("Give us the information and we'll build one")} <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}
